import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { listDocuments, getPreviewHtml } from '../api/documents'
import { getProject } from '../api/projects'
import type { DocumentResponse } from '../types'
import { Layout } from '../components/Layout'
import { Button } from '../components/Button'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { DocumentCard } from './DocumentCard'
import { DownloadSection } from './DownloadSection'
import styles from './DocumentPreview.module.css'

const TEMPLATE_TYPES = new Set(['cert_aislacion', 'informe_tierra', 'cert_equipos', 'decl_jurada'])
type Tab = 'documentos' | 'vista-previa'

export function DocumentPreview() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<Tab>('documentos')
  const [documents, setDocuments] = useState<DocumentResponse[]>([])
  const [projectName, setProjectName] = useState<string>('')
  const [projectAddress, setProjectAddress] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [previewHtml, setPreviewHtml] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [zoom, setZoom] = useState(1)

  const zoomedHtml = useMemo(() => {
    if (!previewHtml) return null
    const imgWidth = Math.round(zoom * 100)
    return previewHtml.replace(
      '</head>',
      `<style>
        .grid { grid-template-columns: 1fr !important; }
        .card img { width: ${imgWidth}% !important; max-width: none !important; }
        .card h3 { font-size: ${0.9 * zoom}rem !important; }
      </style></head>`,
    )
  }, [previewHtml, zoom])

  const load = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError(null)
    try {
      const [docs, project] = await Promise.all([
        listDocuments(id),
        getProject(id),
      ])
      setDocuments(docs)
      setProjectName(project.name)
      setProjectAddress(project.address ?? null)
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number } }
      if (axiosErr?.response?.status === 404) {
        setError('No se encontraron documentos para este proyecto.')
      } else {
        setError('No pudimos cargar los documentos. Intenta nuevamente.')
      }
    } finally {
      setLoading(false)
    }
  }, [id])

  const loadPreview = useCallback(async () => {
    if (!id || previewHtml !== null) return
    setPreviewLoading(true)
    setPreviewError(null)
    try {
      const html = await getPreviewHtml(id)
      setPreviewHtml(html)
    } catch {
      setPreviewError('No se pudo cargar la vista previa. Intenta nuevamente.')
    } finally {
      setPreviewLoading(false)
    }
  }, [id, previewHtml])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (activeTab === 'vista-previa') loadPreview()
  }, [activeTab, loadPreview])

  const generated = documents.filter((d) => !TEMPLATE_TYPES.has(d.type))
  const templates = documents.filter((d) => TEMPLATE_TYPES.has(d.type))

  return (
    <Layout>
      <div className={styles.page}>
        <div className={styles.header}>
          <button
            className={styles.back}
            onClick={() => navigate(`/projects/${id}`)}
            aria-label="Volver al proyecto"
          >
            ← Volver al proyecto
          </button>
          <h1 className={styles.title}>Documentos TE1</h1>
          {projectName && (
            <p className={styles.projectName}>{projectName}</p>
          )}
        </div>

        {/* Tabs */}
        <div className={styles.tabs} role="tablist">
          <button
            role="tab"
            aria-selected={activeTab === 'documentos'}
            className={`${styles.tab} ${activeTab === 'documentos' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('documentos')}
          >
            Documentos
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'vista-previa'}
            className={`${styles.tab} ${activeTab === 'vista-previa' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('vista-previa')}
          >
            Vista previa
          </button>
        </div>

        {loading && (
          <div className={styles.centered}>
            <LoadingSpinner size="lg" message="Cargando documentos..." />
          </div>
        )}

        {!loading && error && (
          <div className={styles.errorBox}>
            <p>{error}</p>
            <Button variant="secondary" onClick={load}>Reintentar</Button>
          </div>
        )}

        {!loading && !error && activeTab === 'documentos' && (
          <>
            {/* ZIP download — sticky in mobile */}
            <div className={styles.zipSection}>
              {id && (
                <DownloadSection
                  projectId={id}
                  projectName={projectName}
                  projectAddress={projectAddress}
                />
              )}
            </div>

            {/* Disclaimer */}
            <div className={styles.disclaimer}>
              <span aria-hidden="true">📋</span>
              <p>
                Este borrador requiere firma y timbre de Instalador SEC autorizado
                antes de presentar ante la SEC
              </p>
            </div>

            {/* Autogenerated */}
            {generated.length > 0 && (
              <section className={styles.section}>
                <h2 className={styles.sectionTitle}>
                  Documentos autogenerados ({generated.length})
                </h2>
                <div className={styles.grid}>
                  {generated.map((doc) => (
                    <DocumentCard key={doc.id} document={doc} projectId={id!} />
                  ))}
                </div>
              </section>
            )}

            {/* Templates */}
            {templates.length > 0 && (
              <section className={styles.section}>
                <h2 className={styles.sectionTitle}>
                  Plantillas pre-llenadas ({templates.length})
                </h2>
                <p className={styles.templateNote}>
                  El electricista debe completar los valores medidos en terreno
                </p>
                <div className={styles.grid}>
                  {templates.map((doc) => (
                    <DocumentCard key={doc.id} document={doc} projectId={id!} />
                  ))}
                </div>
              </section>
            )}

            {documents.length === 0 && (
              <div className={styles.empty}>
                <p>No hay documentos generados aún.</p>
                <Button onClick={() => navigate(`/projects/${id}`)}>
                  Volver al proyecto
                </Button>
              </div>
            )}
          </>
        )}

        {!loading && !error && activeTab === 'vista-previa' && (
          <div className={styles.previewTab}>
            {previewLoading && (
              <div className={styles.centered}>
                <LoadingSpinner size="lg" message="Cargando vista previa..." />
              </div>
            )}
            {previewError && (
              <div className={styles.errorBox}>
                <p>{previewError}</p>
                <Button variant="secondary" onClick={() => { setPreviewHtml(null); loadPreview() }}>
                  Reintentar
                </Button>
              </div>
            )}
            {previewHtml && !previewLoading && (
              <>
                <div className={styles.zoomControls}>
                  <button
                    className={styles.zoomBtn}
                    onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
                    aria-label="Reducir zoom"
                    disabled={zoom <= 0.5}
                  >−</button>
                  <span className={styles.zoomLabel}>{Math.round(zoom * 100)}%</span>
                  <button
                    className={styles.zoomBtn}
                    onClick={() => setZoom((z) => Math.min(2, z + 0.25))}
                    aria-label="Aumentar zoom"
                    disabled={zoom >= 2}
                  >+</button>
                </div>
                <iframe
                  className={styles.previewFrame}
                  title="Vista previa de documentos TE1"
                  srcDoc={zoomedHtml ?? ''}
                  sandbox="allow-same-origin"
                />
              </>
            )}
            {!previewHtml && !previewLoading && !previewError && documents.length === 0 && (
              <div className={styles.empty}>
                <p>Genera los documentos primero para ver la vista previa.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  )
}
